import { Inject, Injectable, NgZone, OnDestroy } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, defer, from, Observable, Subject } from "rxjs";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { UserService } from "../authentication/user.service";
import {
  NovaRealtimeConnectTimings,
  NovaRealtimeEndReason,
  NovaRealtimeEndSessionResponse,
  NovaRealtimeRefusal,
  NovaRealtimeServerEvent,
  NovaRealtimeSession,
  NovaRealtimeStartResult,
  NovaRealtimeState,
} from "./nova-realtime.model";

/**
 * Where the browser hands OpenAI its SDP offer. Taken from the FLIP-254 spike, which is the version
 * that was actually shown to work end to end.
 */
const OPENAI_REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

/** Every phase at zero: a connect that was never attempted because one was already up. */
const EMPTY_CONNECT_TIMINGS: NovaRealtimeConnectTimings = {
  mintMs: 0,
  microphoneMs: 0,
  offerMs: 0,
  sdpMs: 0,
  totalMs: 0,
};

/**
 * FLIP-486. How long the mint, and separately the SDP exchange, may take before the attempt is
 * given up on.
 *
 * Neither call had a ceiling, so after an idle spell - a cold API on the first mint of the day -
 * a request that never answered left the user on "Connecting" indefinitely, with no message and
 * no way to retry. Generous on purpose: a cold start is slow, not broken, and this exists to turn
 * "never" into "a clear failure", not to hurry a connect that is going to work. It deliberately
 * does NOT cover the microphone, whose permission prompt waits on a person.
 */
const CONNECT_TIMEOUT_MS = 20000;

/** The connect phase that outlived `CONNECT_TIMEOUT_MS`. Its own type so callers can tell it apart. */
class ConnectTimeoutError extends Error {
  constructor() {
    super("Nova's voice took too long to connect.");
    this.name = "ConnectTimeoutError";
    Object.setPrototypeOf(this, ConnectTimeoutError.prototype);
  }
}

/** The channel name OpenAI expects. Not ours to choose. */
const OAI_EVENTS_CHANNEL = "oai-events";

/**
 * How far before `expiresAt` the session is wound down.
 *
 * Ending slightly early is the whole point: at the ceiling itself the credential simply stops
 * working, and whatever the user was saying is lost mid-sentence with no explanation. Five seconds
 * is enough to say so and close cleanly, and short enough that nobody feels short-changed.
 */
const EXPIRY_MARGIN_MS = 5000;

/**
 * The microphone constraints the rest of the app already uses.
 *
 * Echo cancellation is load-bearing rather than cosmetic - without it Nova's own voice comes back
 * up the microphone and the server's turn detection treats her as an interruption.
 */
const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/**
 * FLIP-273. Owns the Realtime voice session: the peer connection, the microphone, Nova's audio and
 * the session's lifetime with our server.
 *
 * WHY THIS IS A SERVICE AND NOT PART OF THE CHAT COMPONENT. A voice call outlives the thing that
 * started it. Minimising to the FAB (FLIP-234), navigating to another page, or the dialog being
 * torn down for any other reason all leave the call running today, and a session owned by
 * NovaCompanionChatComponent would die with it - taking the microphone, the audio and the user's
 * concurrency slot with it. Being `providedIn: "root"` is what makes surviving those free rather
 * than something defensive code has to arrange.
 *
 * WHAT THIS SERVICE DOES NOT DO. It does not interpret events, relay them to our server, decide
 * what a tool call should answer, drive captions or decide when voice mode is on. It moves audio
 * and exposes what arrives; every one of those is a separate ticket with its own contract.
 *
 * The outbound half is here for the same reason: the data channel is owned here, so writing to it
 * cannot be done from anywhere else. `sendClientEvent` (FLIP-274) is the raw write,
 * `sendFunctionCallOutput` (FLIP-275) is the framing for a blocking function-call answer, and
 * `sendSilentContextUpdate` (B5) is the framing for an ambient fact that should update what Nova
 * knows without prompting her to speak. All three are transport, not interpretation - what an
 * item SAYS is decided by the caller and merely carried through here.
 */
@Injectable({
  providedIn: "root",
})
export class NovaRealtimeSessionService implements OnDestroy {
  private readonly stateSource = new BehaviorSubject<NovaRealtimeState>("idle");
  /** Where the transport is. Replays, so a late subscriber is not left guessing. */
  readonly state$: Observable<NovaRealtimeState> = this.stateSource.asObservable();

  private readonly eventsSource = new Subject<NovaRealtimeServerEvent>();
  /**
   * Inbound `oai-events` messages, parsed.
   *
   * This is the seam the relay ticket consumes. Deliberately not replayed: a subscriber that
   * arrives mid-session wants what happens next, and replaying a turn's worth of events would have
   * it record turns that were already recorded.
   */
  readonly events$: Observable<NovaRealtimeServerEvent> = this.eventsSource.asObservable();

  private readonly noticeSource = new Subject<string>();
  /**
   * Messages meant for the user, in their own words.
   *
   * Used for the two endings a person needs told about - the session reaching its ceiling, and the
   * connection failing under it. Refusals do NOT come through here: they are part of the start
   * result, because the caller has to decide whether voice even opens.
   */
  readonly notice$: Observable<string> = this.noticeSource.asObservable();

  private readonly remoteStreamSource = new BehaviorSubject<MediaStream | null>(null);
  /**
   * Nova's inbound audio, for anything that needs to LOOK at her voice rather than hear it.
   *
   * FLIP-276. The waveform used to be driven by the MP3 playback queue, which no longer exists on
   * this path - the audio arrives as a live WebRTC track instead. Exposing the stream lets the UI
   * put an analyser on the real thing, so the wave moves because Nova is actually making sound and
   * not because something inferred that she probably is.
   *
   * Replays, so a subscriber that arrives after `ontrack` has already fired - a component opened
   * mid-session, which minimising to the FAB makes ordinary - still gets the stream. Emits null on
   * teardown so a consumer cannot keep analysing a track that has stopped.
   */
  readonly remoteStream$: Observable<MediaStream | null> = this.remoteStreamSource.asObservable();

  private readonly microphoneStreamSource = new BehaviorSubject<MediaStream | null>(null);
  /**
   * The user's own microphone, for the same reason `remoteStream$` exists.
   *
   * FLIP-276. The listening half of the waveform needs amplitude, and the only microphone in the
   * session is this one. Publishing it means the UI analyses the stream that is actually feeding
   * the call rather than opening a SECOND `getUserMedia` beside it - which is what the legacy path
   * does, and which costs another hardware stream and can show a wave that disagrees with what the
   * call is really hearing.
   */
  readonly microphoneStream$: Observable<MediaStream | null> =
    this.microphoneStreamSource.asObservable();

  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private microphone: MediaStream | null = null;
  private audio: HTMLAudioElement | null = null;
  private session: NovaRealtimeSession | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private unloadHandler: (() => void) | null = null;

  /**
   * The connect in progress, shared between callers.
   *
   * FLIP-289. `start()` is `defer`red, so every subscription re-runs `connect()` - and two
   * subscriptions that overlap (anything that calls `start()` twice before the first mint returns)
   * would each see `this.session` still null and mint a SECOND credential, which the server refuses
   * as `concurrent_session`. This coalesces them onto one connect. Held only while a connect runs;
   * a retry AFTER one settles still re-runs the mint, which `start()`'s `defer` exists to allow.
   */
  private connectInFlight: Promise<NovaRealtimeStartResult> | null = null;

  /**
   * Which connect attempt is the live one. Each `connect()` takes the next number; `close()` takes
   * one too, which is what makes an attempt still in flight stale.
   *
   * A hang-up while Nova is still connecting - the user changing their mind - runs `close()`, and
   * `teardown()` pulls the peer connection and session out from under the connect that is still
   * awaiting the mint or the SDP exchange. Without this, that connect then fails on its own
   * teardown, reports it as `unavailable`, and the user is told Nova broke when they are the one
   * who hung up. With it, the connect notices after each await that it has been cancelled, cleans
   * up whatever IT produced in the meantime, and says nothing.
   */
  private connectAttempt = 0;

  /**
   * Whether an `error` frame on this session has already been surfaced to the user.
   *
   * A failing session can emit a burst of these; the first one already ends the call, so the rest
   * would only stack duplicate notices behind it. Reset by `teardown` so the next session starts
   * clean.
   */
  private serverErrorNotified = false;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private userService: UserService,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  /** The running session, or null. The relay ticket reads `conversationId` and `sessionId` from it. */
  get currentSession(): NovaRealtimeSession | null {
    return this.session;
  }

  /** True while there is a session to end. */
  get isActive(): boolean {
    return this.session !== null;
  }

  /**
   * Mints a session and brings up the peer connection.
   *
   * Deferred so that nothing happens until someone subscribes, and so a retry after a refusal
   * re-runs the mint rather than replaying the refusal it was handed the first time.
   */
  start(): Observable<NovaRealtimeStartResult> {
    return defer(() => from(this.beginConnect()));
  }

  /**
   * Ends the session: tears down the local transport, then tells the server.
   *
   * Local teardown happens FIRST and unconditionally. The microphone light going out is the part
   * the user can see, and making it wait on a network call - which may be what is broken - would
   * leave the microphone live while we talk to a server that is not answering.
   */
  end(reason: NovaRealtimeEndReason): Observable<NovaRealtimeEndSessionResponse | null> {
    return defer(() => from(this.close(reason)));
  }

  /**
   * Sends one client event down the `oai-events` channel.
   *
   * FLIP-274. Added for `conversation.item.truncate`, which is the one relayed event the client
   * originates rather than observes: the server has no audio, so only the browser can say where a
   * reply was cut, and the event sent HERE is what actually stops the audio and trims the model's
   * own context. Without it the model would carry on believing the user heard the whole reply.
   *
   * Returns false when there is no open channel to write to, which is an ordinary race rather than
   * an error - a barge-in and a hang-up can land in either order, and the caller decides what a
   * failed send means. Throwing would take out the relay's event subscription with it.
   */
  sendClientEvent(event: Record<string, unknown>): boolean {
    const channel = this.dataChannel;
    if (!channel || channel.readyState !== "open") {
      return false;
    }

    try {
      channel.send(JSON.stringify(event));
      return true;
    } catch {
      // A channel that closed between the readyState check and the write. Same answer as above.
      return false;
    }
  }

  /**
   * Writes one conversation item, without prompting Nova for a reply.
   *
   * The primitive both `sendFunctionCallOutput` and `sendSilentContextUpdate` build on - the only
   * difference between the two is whether a `response.create` follows this. `item` is passed
   * through untouched, same contract as `sendFunctionCallOutput`'s `output`: this frames the write,
   * it does not decide what the write says.
   */
  private sendConversationItem(item: Record<string, unknown>): boolean {
    return this.sendClientEvent({
      type: "conversation.item.create",
      item,
    });
  }

  /**
   * Answers a function call: the output item, then the `response.create` that lets the model speak
   * again.
   *
   * BOTH ARE REQUIRED. The output item alone parks the answer in the conversation without resuming
   * the turn, and the model sits silent waiting for a cue that never comes.
   *
   * `output` is passed through untouched. It is the JSON the server already serialised, and it is
   * sent verbatim because only the server has checked the model's title against the Learn
   * catalogue (FLIP-224).
   *
   * FLIP-275. Built on FLIP-274's `sendClientEvent` rather than beside it: both write the same
   * channel, and two ways to do that is how the readyState check comes to be done in one of them
   * and not the other.
   */
  sendFunctionCallOutput(callId: string, output: string): boolean {
    const sent = this.sendConversationItem({
      type: "function_call_output",
      call_id: callId,
      output,
    });

    if (!sent) {
      return false;
    }

    return this.sendClientEvent({ type: "response.create" });
  }

  /**
   * Updates what Nova knows without making her say anything about it.
   *
   * B5's invariant (Nova Architecture Brief, Section 2): "update Nova's context ≠ make Nova
   * speak". `sendFunctionCallOutput` always pairs the item with a `response.create` because a
   * function call is blocking - the model is waiting on it. An ambient fact - a screen changed, a
   * save succeeded - is not; writing it here only makes it part of what Nova knows for whatever she
   * says next on her own initiative. Deliberately does NOT arrange a `response.create` anywhere,
   * not even on a delay: that would just be `sendFunctionCallOutput` under another name, and the
   * whole point of this primitive is that a fact becoming known and Nova remarking on it are two
   * separate decisions.
   *
   * `item` is caller-defined and passed through untouched - this has no opinion on whether it is a
   * `message`, what role it carries, or how the fact is worded, only that it goes in silently.
   */
  sendSilentContextUpdate(item: Record<string, unknown>): boolean {
    const sent = this.sendConversationItem(item);
    // eslint-disable-next-line no-console
    console.debug("[nova-realtime] silent context update %s — %o", sent ? "sent" : "NOT sent", item);
    return sent;
  }

  /**
   * B6. Arms the next walkthrough step: tells Nova what it is, without making her speak it.
   *
   * A THIN NAME OVER `sendSilentContextUpdate`, not a second transport - "arm, don't speak" (the
   * Nova Architecture Brief, Section 5) is exactly what the silent primitive already gives B5's
   * general case, and B6 rides on it rather than inventing its own. When `next_step()` is
   * eventually called, Nova speaks from what this already put in her context, in her own words -
   * she is not handed a script to recite.
   */
  sendWalkthroughStep(text: string): boolean {
    return this.sendSilentContextUpdate({
      type: "message",
      role: "system",
      content: [{ type: "input_text", text }],
    });
  }

  ngOnDestroy(): void {
    // Reached when the app itself is going away. The unload handler covers the server side; this
    // is here so the microphone is released even in a teardown that is not a page unload.
    this.teardown();
  }

  /**
   * Coalesces concurrent `start()` subscriptions onto one connect - see `connectInFlight`. Cleared
   * the moment that connect settles, so a `start()` after a failure runs a fresh attempt.
   *
   * A connect that was CANCELLED is the one case a joiner does not inherit. Stop-then-start-again
   * lands here: the stop cancelled the connect still in flight, and the new `start()` arrives
   * before it has noticed. Handing that caller the `cancelled` verdict would answer a start with
   * "you hung up", and nothing would ever connect. So the joiner waits for the stale attempt to
   * finish cleaning up - it must, before a fresh one touches the same microphone and peer - and
   * then runs its own.
   */
  private async beginConnect(): Promise<NovaRealtimeStartResult> {
    for (;;) {
      const inFlight = this.connectInFlight;
      if (!inFlight) {
        break;
      }
      const result = await inFlight;
      if (result.outcome !== "cancelled") {
        return result;
      }
    }

    this.connectInFlight = this.connect();
    try {
      return await this.connectInFlight;
    } finally {
      this.connectInFlight = null;
    }
  }

  /**
   * Rejects with `ConnectTimeoutError` if `work` has not settled within `CONNECT_TIMEOUT_MS`.
   * Does not cancel `work` - callers that care what it eventually produces handle that themselves.
   */
  private withConnectTimeout<T>(work: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new ConnectTimeoutError()), CONNECT_TIMEOUT_MS);
      work.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /** True once `close()` has run since this attempt began - the user hung up under it. */
  private isCancelled(attempt: number): boolean {
    return attempt !== this.connectAttempt;
  }

  private async connect(): Promise<NovaRealtimeStartResult> {
    if (this.session) {
      // Already running. Returning the live session rather than minting a second one, which the
      // server would refuse with concurrent_session anyway - and that refusal would be ours, not
      // the user's doing. Nothing was connected, so every phase is zero.
      return { outcome: "started", session: this.session, timings: EMPTY_CONNECT_TIMINGS };
    }

    const attempt = ++this.connectAttempt;
    this.setState("connecting");

    // Mobile audio unlock (spike). `connect()` runs synchronously from here up to its first
    // `await` below - which is also, transitively, the synchronous call stack of the tap that
    // opened voice mode, since `start()` is `defer`red and Angular's (click) binding subscribes
    // synchronously. This is the ONLY point that is true. `ontrack` fires much later, once the
    // SDP/ICE handshake has settled, and calling `audio.play()` there - as the code already did -
    // is calling it from outside any user gesture. Desktop Chrome lets that through; iOS Safari
    // (and every iOS browser, all WebKit) refuses it with NotAllowedError, and the refusal was
    // being swallowed, so the call connected with a live microphone and no way to hear Nova back.
    // Priming the SAME element here, still inside the gesture, is what "blesses" it so the later,
    // async `.play()` in `ontrack` is allowed to proceed once `srcObject` is actually set.
    this.primeAudioElement();

    // FLIP-285. STARTED TOGETHER, and the order of these two lines is the whole point.
    //
    // getUserMedia has no dependency on the ephemeral secret, but it used to wait for the entire
    // mint - our server resolving the conversation, assembling the instructions and calling
    // OpenAI - before it began. Opening the audio device with echo cancellation and noise
    // suppression negotiated is not free even when permission was granted long ago, so that time
    // was being spent twice over end to end. Now the wait is max(mint, microphone) rather than
    // their sum.
    // FLIP-285 instrumentation. Each phase is timed from here so the 2s connect can be attributed
    // rather than guessed at. `timings` is mutated as the phases complete and read once at the end.
    const startedAt = Date.now();
    const timings: NovaRealtimeConnectTimings = { ...EMPTY_CONNECT_TIMINGS };

    const microphonePromise = this.timePhase(this.requestMicrophone(), (ms) => {
      timings.microphoneMs = ms;
    });
    const sessionPromise = this.timePhase(this.mint(), (ms) => {
      timings.mintMs = ms;
    });

    // FLIP-289. The LOCAL half of the handshake - the peer connection, the microphone track, the
    // data channel and the SDP offer - has no dependency on the minted secret; only the SDP
    // EXCHANGE below does. Building it here, alongside the mint, is what takes `createOffer` and
    // `setLocalDescription` off the critical path: they used to wait the whole ~1s mint out before
    // starting. Same move FLIP-285 made for `getUserMedia`, one step further down the handshake.
    const localPeerPromise = this.prepareLocalPeer(microphonePromise, timings);

    // Whichever settles second would otherwise reject with nobody awaiting it, and an unhandled
    // rejection here is a console error in a session that recovered perfectly well. The real
    // handling is below; these only mark them as claimed.
    microphonePromise.catch(() => undefined);
    sessionPromise.catch(() => undefined);
    localPeerPromise.catch(() => undefined);

    let session: NovaRealtimeSession;
    try {
      session = await this.withConnectTimeout(sessionPromise);
    } catch (error) {
      if (error instanceof ConnectTimeoutError) {
        // The mint is still out there and may yet claim a slot nobody will use. Hand it straight
        // back if it ever lands, or the user's retry is refused as concurrent_session.
        sessionPromise.then(
          (late) => this.endOnServer(late, "closed"),
          () => undefined
        );
      }
      // The microphone and a half-built peer connection may both be in flight for a session that
      // will never exist. Letting them go would leave the device light on, and a connection open,
      // with nothing behind either.
      await this.releasePendingMicrophone(microphonePromise);
      await this.discardPendingLocalPeer(localPeerPromise);
      if (this.isCancelled(attempt)) {
        // The user hung up before the mint came back, and the mint then failed. Whatever it
        // failed with is not news to someone who has already left - and `close()` set the state.
        return { outcome: "cancelled" };
      }
      this.setState("idle");
      return this.asStartFailure(error);
    }

    if (this.isCancelled(attempt)) {
      // The user hung up while the mint was in flight. `close()` ran with no session to end, so
      // the slot this mint has just claimed is this connect's to hand back - the server does not
      // know the user changed their mind - along with the microphone and peer that may have
      // finished opening since `teardown()` looked. Nothing was connected, so no notice: the one
      // thing that happened here is the thing the user asked for.
      await this.releasePendingMicrophone(microphonePromise);
      await this.discardPendingLocalPeer(localPeerPromise);
      await this.endOnServer(session, "closed");
      return { outcome: "cancelled" };
    }

    // Held BEFORE the connection is finished, so that a failure below has something to end. The
    // slot is claimed the moment the secret is minted, not the moment audio flows, and `close`
    // needs the session id to hand it back.
    this.session = session;

    try {
      await this.completePeerConnection(session, localPeerPromise, timings);
    } catch (error) {
      if (this.isCancelled(attempt)) {
        // `close()` ended the session and tore the peer down while the SDP exchange was out - that
        // teardown is what threw. The slot is already handed back; `teardown()` again only catches a
        // microphone or peer that finished opening after the hang-up and so escaped the first one.
        this.teardown();
        return { outcome: "cancelled" };
      }
      // The secret was minted, so a slot is held even though no audio ever flowed. Ending it is
      // what stops a failed connection from locking the user out of voice with concurrent_session
      // for the rest of the window.
      await this.close("error");
      this.setState("failed");
      this.noticeSource.next("Nova couldn't connect. Please try again.");
      return {
        outcome: "unavailable",
        message: this.describeConnectionFailure(error),
      };
    }

    if (this.isCancelled(attempt)) {
      // Connected AFTER the user hung up: the microphone and peer finished opening once `close()`
      // had already looked, so the exchange went through on a transport nothing had closed. Left
      // alone this is a live call with a hot microphone behind a UI that says voice is off. The
      // server side was ended by `close()`, which saw `this.session`; the local side is ended here.
      this.teardown();
      return { outcome: "cancelled" };
    }

    this.armExpiry(session);
    this.armUnloadBeacon();
    this.setState("connected");

    timings.totalMs = Date.now() - startedAt;
    this.reportConnectTimings(timings);

    return { outcome: "started", session, timings };
  }

  /**
   * Stops a microphone that was opened for a session that then failed to mint.
   *
   * Awaits the promise rather than ignoring it: the device may still be opening, and a stream that
   * arrives after we have given up is exactly the one nothing else will ever close. A rejection is
   * swallowed, because a microphone that never opened is the outcome we wanted anyway.
   */
  private async releasePendingMicrophone(microphonePromise: Promise<MediaStream>): Promise<void> {
    try {
      const stream = await microphonePromise;
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      // Nothing to release.
    }
  }

  /**
   * Records how long a phase took, without changing what the promise does.
   *
   * Returns the SAME settled value or rejection, so a timing wrapper can never be the reason a
   * connect behaves differently. A phase that fails is left at zero rather than recorded, because
   * "how long did the microphone take" has no useful answer when it never opened.
   */
  private timePhase<T>(work: Promise<T>, record: (ms: number) => void): Promise<T> {
    const startedAt = Date.now();
    return work.then((value) => {
      record(Date.now() - startedAt);
      return value;
    });
  }

  /**
   * One structured line per connect, so a slow one can be attributed from a browser console during
   * testing without a server round trip.
   *
   * DELIBERATELY NOT SENT ANYWHERE YET. Persisting these belongs with the per-session record in
   * FLIP-271, which is where a session's cost and duration already go; inventing a second
   * telemetry endpoint for four numbers would be the wrong shape. Until then this is a testing
   * aid, and it says so.
   */
  private reportConnectTimings(timings: NovaRealtimeConnectTimings): void {
    // eslint-disable-next-line no-console
    console.info(
      "[nova-realtime] connect %dms — mint %dms, microphone %dms (parallel), offer %dms (parallel), sdp %dms",
      timings.totalMs,
      timings.mintMs,
      timings.microphoneMs,
      timings.offerMs,
      timings.sdpMs
    );
  }

  /** POST /api/Nova/StartRealtimeSession. Goes through HttpClient, so it carries our JWT. */
  private mint(): Promise<NovaRealtimeSession> {
    return this.http
      .post<NovaRealtimeSession>(`${this.apiBaseUrl}/api/Nova/StartRealtimeSession`, {})
      .toPromise();
  }

  /**
   * Turns a failed mint into the outcome the caller has to act on.
   *
   * The 429 branch reads `reason`, never the message text: the message is written for a person and
   * is free to change, while `concurrent_session` and `hourly_limit` are the contract.
   */
  private asStartFailure(error: unknown): NovaRealtimeStartResult {
    if (error instanceof ConnectTimeoutError) {
      return {
        outcome: "unavailable",
        message: "Nova is taking too long to connect. Please try again.",
      };
    }

    if (error instanceof HttpErrorResponse && error.status === 429) {
      const body = (error.error ?? {}) as Partial<NovaRealtimeRefusal>;
      return {
        outcome: "refused",
        refusal: {
          reason: body.reason ?? "hourly_limit",
          message: body.message ?? "Nova's voice isn't available right now.",
          retryAfterSeconds: body.retryAfterSeconds ?? 0,
        },
      };
    }

    if (error instanceof HttpErrorResponse) {
      // FLIP-487. Status 0 is the browser never getting an answer - offline, a dropped network, a
      // blocked request - which is the user's to fix and unlike a server fault.
      if (error.status === 0) {
        return {
          outcome: "unavailable",
          message: "Nova's voice couldn't be reached. Check your connection and try again.",
        };
      }

      // The server now says WHY (busy, not working on our side, unexpected) and what to do next,
      // in words written for the person. An older server's plain-text body is not an object and
      // falls through to the generic line below.
      const body = error.error as Partial<NovaRealtimeRefusal> | string | null;
      if (body && typeof body === "object" && typeof body.message === "string" && body.message) {
        return { outcome: "unavailable", message: body.message };
      }
    }

    return {
      outcome: "unavailable",
      message: "Nova's voice is unavailable right now. Please try again, or keep typing here.",
    };
  }

  /**
   * The LOCAL half of the WebRTC handshake: the peer connection, the microphone track, the
   * `oai-events` data channel and the SDP offer. Returns the offer SDP for `completePeerConnection`
   * to exchange.
   *
   * NONE OF THIS NEEDS THE MINTED SECRET, which is why FLIP-289 runs it alongside the mint rather
   * than after it. The microphone is awaited FIRST because it is far and away the likeliest step to
   * fail - a denied permission or a device another tab already holds - and failing there leaves
   * nothing to unwind. The handshake order the FLIP-254 spike established is otherwise unchanged;
   * it is only split across the mint.
   */
  private async prepareLocalPeer(
    microphonePromise: Promise<MediaStream>,
    timings: NovaRealtimeConnectTimings
  ): Promise<string> {
    // Already in flight since connect() - see the note there. Awaiting a promise that has usually
    // resolved by now is what removes getUserMedia from the critical path; a failure still throws
    // here, so the caller's teardown is unchanged.
    this.microphone = await microphonePromise;
    this.microphoneStreamSource.next(this.microphone);

    const pc = this.createPeerConnection();
    this.peerConnection = pc;

    // Nova's voice. Attached to an element this service owns and never puts in the DOM, so that
    // navigating away - which destroys any component's template - does not stop the audio. This
    // is the SAME element `primeAudioElement` played (empty) inside the tap that started connect()
    // - reusing it, rather than creating a fresh one here, is what carries that gesture forward.
    pc.ontrack = (event: RTCTrackEvent) => {
      const audio = this.audio ?? (this.audio = this.createAudioElement());
      audio.srcObject = event.streams[0];
      // Published as well as played: FLIP-276's waveform analyses this stream directly.
      this.remoteStreamSource.next(event.streams[0] ?? null);
      const played = audio.play();
      if (played && typeof played.catch === "function") {
        // Still swallowed rather than surfaced: even primed, a play() can fail for reasons that
        // have nothing to do with the gesture (the device muted, another app holding the output),
        // and there is nothing useful to do about it from here beyond not throwing out of an
        // event handler.
        played.catch(() => undefined);
      }
    };

    const track = this.microphone.getAudioTracks()[0];
    if (track) {
      pc.addTrack(track, this.microphone);
    }

    const channel = pc.createDataChannel(OAI_EVENTS_CHANNEL);
    this.dataChannel = channel;
    channel.onmessage = (event: MessageEvent) => this.onChannelMessage(event);

    const offerStartedAt = Date.now();
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    timings.offerMs = Date.now() - offerStartedAt;

    return offer.sdp ?? "";
  }

  /**
   * The rest of the handshake, once the secret exists: hand OpenAI the offer built by
   * `prepareLocalPeer` and apply the answer.
   *
   * Ordered after the mint because `exchangeSdp` is the ONE step that needs the client secret. By
   * the time this runs the offer is usually already built, so the mint and the offer were paid for
   * in parallel rather than back to back (FLIP-289).
   */
  private async completePeerConnection(
    session: NovaRealtimeSession,
    localPeerPromise: Promise<string>,
    timings: NovaRealtimeConnectTimings
  ): Promise<void> {
    // Usually already resolved - the offer was built while the mint ran. A failure here (denied
    // microphone, a device another tab holds) still throws, so the caller's teardown is unchanged.
    const offerSdp = await localPeerPromise;

    const pc = this.peerConnection;
    if (!pc) {
      // A teardown - a hang-up, or the sheet closing - landed between the offer being built and
      // the secret coming back. There is nothing to connect any more.
      throw new Error("The peer connection was torn down before the session was minted.");
    }

    const sdpStartedAt = Date.now();
    const answerSdp = await this.withConnectTimeout(
      this.exchangeSdp(offerSdp, session.clientSecret)
    );
    timings.sdpMs = Date.now() - sdpStartedAt;

    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
  }

  /**
   * Tears down a peer connection that was being built for a session whose mint then failed.
   *
   * Mirrors `releasePendingMicrophone`: the connection and its data channel may finish setting
   * themselves up moments after we have given up, and nothing else would ever close them. Awaits
   * the promise so a connection that lands late is still caught, then lets `teardown` - null-safe
   * and idempotent - close whatever was built.
   */
  private async discardPendingLocalPeer(localPeerPromise: Promise<string>): Promise<void> {
    try {
      await localPeerPromise;
    } catch {
      // It never got far enough to hold anything.
    }
    this.teardown();
  }

  /**
   * The SDP exchange with OpenAI.
   *
   * DELIBERATELY `fetch` AND NOT HttpClient. JwtInterceptor puts this user's flip* bearer token on
   * every HttpClient request with no allowlist, so routing this through it would send our JWT to
   * OpenAI and overwrite the ephemeral secret's Authorization header at the same time. Neither is
   * acceptable, and neither would be obvious from the call site.
   */
  private async exchangeSdp(offerSdp: string, clientSecret: string): Promise<string> {
    const response = await fetch(OPENAI_REALTIME_CALLS_URL, {
      method: "POST",
      body: offerSdp,
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        "Content-Type": "application/sdp",
      },
    });

    if (!response.ok) {
      throw new Error(`SDP exchange failed: ${response.status}`);
    }

    return response.text();
  }

  /**
   * Data channel messages arrive outside Angular's zone, so anything downstream of `events$` would
   * otherwise update without a change detection pass - the classic "the data is right but the
   * screen is a turn behind" bug. A malformed frame is dropped rather than thrown, because
   * throwing here kills the channel for every later event too.
   */
  private onChannelMessage(event: MessageEvent): void {
    let parsed: NovaRealtimeServerEvent;
    try {
      parsed = JSON.parse(event.data);
    } catch {
      return;
    }

    this.zone.run(() => {
      if (parsed.type === "error") {
        this.handleServerError(parsed);
      }
      // Still forwarded: a consumer that wants the raw error frame can have it, and dropping it
      // here would only make this path harder to reason about.
      this.eventsSource.next(parsed);
    });
  }

  /**
   * An `error` frame on the `oai-events` channel.
   *
   * These are otherwise INVISIBLE. The relay and the voice UI both fall through on an event type
   * they do not recognise, so a model that will not answer a turn - a rate limit against the
   * organisation's shared TPM pool, a rejected session config, a credential that lapsed mid-call -
   * leaves the status bar sitting on "listening" with no reply ever arriving and nothing said
   * about why.
   *
   * Every error is logged in full; an error frame carries no secret. The FIRST one also ends the
   * session with a message the user can act on - by the time one of these reaches here the turn is
   * almost always dead, and "start again" beats a frozen screen. `end` as well as the notice, so
   * the concurrency slot is handed back even if nothing is subscribed to `notice$` (a torn-down
   * component). Latched by `serverErrorNotified` so a burst does not stack notices.
   */
  private handleServerError(event: NovaRealtimeServerEvent): void {
    const detail = (event["error"] ?? {}) as {
      type?: string;
      code?: string;
      message?: string;
    };

    // eslint-disable-next-line no-console
    console.error(
      "[nova-realtime] server error — type: %s, code: %s, message: %s",
      detail.type ?? "(none)",
      detail.code ?? "(none)",
      detail.message ?? "(none)",
      event
    );

    if (this.serverErrorNotified) {
      return;
    }
    this.serverErrorNotified = true;

    // `end` before the notice: the notice is what the component turns into its own teardown, and
    // ending here first means the server's EndRealtimeSession records `error` rather than the
    // `closed` the component would send. Both are safe - the second `end` finds no session.
    this.end("error").subscribe();
    this.noticeSource.next(
      "Nova ran into a problem and the voice session has ended. You can start it again."
    );
  }

  /**
   * Winds the session down just before its ceiling, with something the user can read.
   *
   * The alternative - letting `expiresAt` pass - surfaces as the credential going dead, which the
   * page can only report as a connection failure. The ticket is explicit that the ceiling should
   * not be discovered as a handshake error.
   */
  private armExpiry(session: NovaRealtimeSession): void {
    const expiresAt = Date.parse(session.expiresAt);
    if (isNaN(expiresAt)) {
      return;
    }

    const delay = Math.max(0, expiresAt - Date.now() - EXPIRY_MARGIN_MS);
    this.clearExpiry();
    // Outside the zone so an armed timer does not hold a periodic task open for the whole session;
    // the callback re-enters through zone.run to publish.
    this.expiryTimer = this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        this.zone.run(() => {
          this.noticeSource.next(
            "This voice session has reached its time limit. You can start another one."
          );
          this.end("expired").subscribe();
        });
      }, delay)
    );
  }

  private clearExpiry(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  /**
   * Frees the session when the page goes away.
   *
   * `pagehide` rather than `beforeunload`: it is the one that fires reliably on mobile Safari,
   * where a backgrounded tab is the commonest way a call ends without anyone pressing anything.
   *
   * NO REASON IS SENT. An unload handler genuinely does not know why the page is going away, and
   * the server records a missing reason as `unknown` - which is the honest answer, and better than
   * labelling every closed laptop a deliberate `closed`.
   */
  private armUnloadBeacon(): void {
    this.disarmUnloadBeacon();
    this.unloadHandler = () => this.sendUnloadBeacon();
    window.addEventListener("pagehide", this.unloadHandler);
  }

  private disarmUnloadBeacon(): void {
    if (this.unloadHandler) {
      window.removeEventListener("pagehide", this.unloadHandler);
      this.unloadHandler = null;
    }
  }

  /**
   * NOT navigator.sendBeacon. EndRealtimeSession is JWT-gated, sendBeacon cannot set an
   * Authorization header, and a rejected beacon would hold the user's concurrency slot until the
   * server's own timer cleared it - the exact problem FLIP-257 exists to solve. A keepalive fetch
   * outlives the page the same way and can carry the token.
   */
  private sendUnloadBeacon(): void {
    const session = this.session;
    if (!session) {
      return;
    }

    const token = this.userService.getAuthToken();
    fetch(`${this.apiBaseUrl}/api/Nova/EndRealtimeSession`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ sessionId: session.sessionId }),
    }).catch(() => undefined);
  }

  /**
   * Ends the session locally and then on the server.
   *
   * Resolves to null when there was nothing to end or the call failed. A failed close is not worth
   * surfacing: the session is over either way from here, and the server's own ceiling will clear
   * the slot.
   */
  private async close(
    reason: NovaRealtimeEndReason
  ): Promise<NovaRealtimeEndSessionResponse | null> {
    // Any connect still in flight is now stale - see `connectAttempt`. Taken before the teardown
    // so that the connect, when it next looks, already knows the teardown was a hang-up.
    this.connectAttempt++;

    const session = this.session;
    this.setState(session ? "ending" : "idle");
    this.teardown();

    if (!session) {
      return null;
    }

    try {
      return await this.endOnServer(session, reason);
    } finally {
      this.setState("idle");
    }
  }

  /**
   * Hands a minted session's slot back. Resolves to null when the call failed, for the reason
   * `close()` gives; split out because a connect cancelled after its mint has a session to end
   * that `this.session` never held.
   */
  private async endOnServer(
    session: NovaRealtimeSession,
    reason: NovaRealtimeEndReason
  ): Promise<NovaRealtimeEndSessionResponse | null> {
    try {
      const result = await this.http
        .post<NovaRealtimeEndSessionResponse>(
          `${this.apiBaseUrl}/api/Nova/EndRealtimeSession`,
          { sessionId: session.sessionId, reason }
        )
        .toPromise();
      return result ?? null;
    } catch {
      return null;
    }
  }

  /** Releases everything held locally. Safe to call twice - teardown races a close and an unload. */
  private teardown(): void {
    this.clearExpiry();
    this.disarmUnloadBeacon();

    if (this.dataChannel) {
      this.dataChannel.onmessage = null;
      this.dataChannel.close();
      this.dataChannel = null;
    }

    if (this.peerConnection) {
      this.peerConnection.ontrack = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.microphone) {
      // The microphone indicator stays lit until every track is stopped, and a live indicator after
      // a call has ended reads as the app still listening.
      this.microphone.getTracks().forEach((track) => track.stop());
      this.microphone = null;
    }

    if (this.microphoneStreamSource.value !== null) {
      this.microphoneStreamSource.next(null);
    }

    if (this.audio) {
      this.audio.pause();
      this.audio.srcObject = null;
      this.audio = null;
    }

    // After the audio element that played it, so nothing is left holding a stopped track.
    if (this.remoteStreamSource.value !== null) {
      this.remoteStreamSource.next(null);
    }

    this.serverErrorNotified = false;
    this.session = null;
  }

  private describeConnectionFailure(error: unknown): string {
    const name = (error as { name?: string })?.name;
    if (error instanceof ConnectTimeoutError) {
      return "Nova is taking too long to connect. Please try again.";
    }
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      return "Nova needs microphone access to talk with you.";
    }
    if (name === "NotReadableError" || name === "AbortError") {
      // Overwhelmingly this is another tab or app holding the device, which is worth saying out
      // loud - "NotReadableError" tells the user nothing they can act on.
      return "Your microphone is in use by something else. Close it and try again.";
    }
    return "Nova couldn't connect. Please try again.";
  }

  private setState(state: NovaRealtimeState): void {
    if (this.stateSource.value !== state) {
      this.stateSource.next(state);
    }
  }

  // The three seams below exist so the suite can substitute fakes. Headless Chrome has no
  // microphone and no peer to answer an offer, and a service that reached for the globals directly
  // could only be tested by monkey-patching window.

  /** @internal */
  protected createPeerConnection(): RTCPeerConnection {
    return new RTCPeerConnection();
  }

  /** @internal */
  protected requestMicrophone(): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
  }

  /** @internal */
  protected createAudioElement(): HTMLAudioElement {
    const audio = new Audio();
    audio.autoplay = true;
    return audio;
  }

  /**
   * Mobile audio unlock (spike, see the call site in `connect()`).
   *
   * Calls `.play()` on the element before it has anything to play, purely to spend the user
   * gesture on it. An empty `<audio>` element playing is a no-op on every engine this has been
   * checked against - there is nothing to hear either way - so this has no effect on desktop,
   * where the later `ontrack` play() already worked. Idempotent: a second session reuses the same
   * (already-primed) element, and priming it again is harmless.
   */
  primeAudioElement(): void {
    const audio = this.audio ?? (this.audio = this.createAudioElement());
    const primed = audio.play();
    if (primed && typeof primed.catch === "function") {
      primed.catch(() => undefined);
    }
  }
}
